import { useQuery } from '@tanstack/react-query';
import { getProgramProgressCells } from '../../services/programProgressService';
import { loadProgramProgressCellsCache } from '../../utils/programProgressCellsCache';
import { schoolKeys } from '../keys';
import { cacheOnlyQueryOptions } from './cacheOnlyQuery';
import { lsInitialDataOptions } from './lsInitialData';
import type { SchoolGradeTermScope } from './types';

export type UseProgramProgressCellsQueryParams = SchoolGradeTermScope & {
  enabled?: boolean;
  forceRefresh?: boolean;
  mondayIsoFrom?: string;
  mondayIsoTo?: string;
  cacheOnly?: boolean;
};

export function programProgressCellsQueryOptions(params: UseProgramProgressCellsQueryParams) {
  const {
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    enabled = true,
    forceRefresh = false,
    mondayIsoFrom,
    mondayIsoTo,
    cacheOnly = false,
  } = params;
  const ls = loadProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId);
  const canSeedFromLs = ls.found && ls.coverage !== 'partial' && !mondayIsoFrom && !mondayIsoTo;
  const queryKey =
    mondayIsoFrom && mondayIsoTo
      ? ([
          ...schoolKeys.programProgressCells(workspaceId, academicYear, termNumber, gradeId),
          mondayIsoFrom,
          mondayIsoTo,
        ] as const)
      : schoolKeys.programProgressCells(workspaceId, academicYear, termNumber, gradeId);

  return {
    queryKey,
    enabled: enabled && !!workspaceId.trim() && !!gradeId.trim(),
    ...lsInitialDataOptions(canSeedFromLs, ls.rows),
    ...cacheOnlyQueryOptions(cacheOnly),
    queryFn: () =>
      getProgramProgressCells(workspaceId, academicYear, termNumber, gradeId, {
        forceRefresh: cacheOnly ? false : forceRefresh,
        mondayIsoFrom,
        mondayIsoTo,
      }),
  };
}

export function useProgramProgressCellsQuery(params: UseProgramProgressCellsQueryParams) {
  return useQuery(programProgressCellsQueryOptions(params));
}
